import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Send, ThumbsUp, Check, ChevronDown, ChevronUp, Play, HelpCircle, Lightbulb, Trophy, BookOpen, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { formatLevel } from '@/lib/levels';
import { getBannerForPosition, PromoBanner, fetchBanners } from '@/components/PromoBanner';
import LamnsoKeyboard from '@/components/LamnsoKeyboard';
import api from '@/lib/api';

const TYPE_CONFIG = {
  question: { label: 'Question', icon: HelpCircle, color: 'bg-[#E8F5E9] text-[#4CAF50]' },
  tip: { label: 'Tip', icon: Lightbulb, color: 'bg-[#FDF6F0] text-[#D4A373]' },
  progress: { label: 'Progress', icon: Trophy, color: 'bg-[#EAF1EA] text-[#588157]' },
  proverb: { label: 'Proverb', icon: BookOpen, color: 'bg-[#F5F5F4] text-[#57534E]' },
  cultural_note: { label: 'Culture', icon: BookOpen, color: 'bg-[#FDF6F0] text-[#D4A373]' },
  video_share: { label: 'Video', icon: Film, color: 'bg-[#ECFEFF] text-[#0891B2]' },
  general: { label: 'Post', icon: MessageCircle, color: 'bg-[#F5F5F4] text-[#57534E]' },
};

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [postType, setPostType] = useState('general');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [activeTab, setActiveTab] = useState('feed'); // feed | suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [sugLoading, setSugLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [activeInput, setActiveInput] = useState('post'); // post | comment

  useEffect(() => { loadPosts(); fetchBanners().then(setBanners); }, [filter]); // eslint-disable-line
  useEffect(() => { if (activeTab === 'suggestions') loadSuggestions(); }, [activeTab]); // eslint-disable-line

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/community/posts?type=${filter}&limit=30`);
      setPosts(res.data.posts || []);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const loadSuggestions = async () => {
    setSugLoading(true);
    try {
      const res = await api.get('/suggestions?status=pending&limit=30');
      setSuggestions(res.data.suggestions || []);
    } catch (err) { console.error(err) }
    setSugLoading(false);
  };

  const voteSuggestion = async (suggestionId, vote) => {
    try {
      const res = await api.post(`/suggestions/${suggestionId}/vote`, { vote });
      setSuggestions(prev => prev.map(s =>
        s.suggestion_id === suggestionId
          ? { ...s, vote_score: res.data.vote_score, weighted_vote_score: res.data.weighted_vote_score, user_vote: vote }
          : s
      ));
    } catch (err) { console.error(err) }
  };

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      const res = await api.post('/community/posts', { content: newPost, type: postType, youtube_url: youtubeUrl });
      setPosts([res.data, ...posts]);
      setNewPost('');
      setYoutubeUrl('');
    } catch (err) { console.error(err) }
    setPosting(false);
  };

  const handleLike = async (postId) => {
    try {
      const res = await api.post(`/community/posts/${postId}/like`);
      setPosts(posts.map(p => p.post_id === postId ? {
        ...p, likes_count: res.data.liked ? (p.likes_count || 0) + 1 : (p.likes_count || 0) - 1, user_liked: res.data.liked
      } : p));
    } catch (err) { console.error(err) }
  };

  const handleHelpful = async (targetId) => {
    try {
      const res = await api.post(`/community/react/${targetId}`, { reaction_type: 'helpful' });
      setPosts(posts.map(p => p.post_id === targetId ? {
        ...p, helpful_count: res.data.reacted ? (p.helpful_count || 0) + 1 : (p.helpful_count || 0) - 1, user_helpful: res.data.reacted
      } : p));
    } catch (err) { console.error(err) }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  };

  const typeButtons = ['general', 'question', 'tip', 'proverb', 'progress'];
  const filterButtons = [
    { key: '', label: 'All' },
    { key: 'question', label: 'Questions' },
    { key: 'tip', label: 'Tips' },
    { key: 'proverb', label: 'Proverbs' },
    { key: 'video_share', label: 'Videos' },
  ];

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-20 sm:max-w-2xl">
      <h1 className="text-xl font-bold text-[#1C1917] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="community-title">
        Community
      </h1>

      {/* Main tabs: Feed vs Suggestions */}
      <div className="flex gap-2 mb-4" data-testid="community-tabs">
        <button onClick={() => setActiveTab('feed')}
          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-center transition-colors ${
            activeTab === 'feed' ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
          }`} data-testid="tab-feed">
          Feed
        </button>
        <button onClick={() => setActiveTab('suggestions')}
          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-center transition-colors ${
            activeTab === 'suggestions' ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
          }`} data-testid="tab-suggestions">
          Translation Suggestions
        </button>
      </div>

      {activeTab === 'suggestions' ? (
        /* SUGGESTIONS TAB */
        <div data-testid="suggestions-tab">
          <p className="text-xs text-[#57534E] mb-4">
            Vote on community-suggested translations. Wirfon-approved suggestions become part of LámFon Translate.
          </p>
          {sugLoading ? (
            <div className="text-center py-12"><div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" /></div>
          ) : suggestions.length > 0 ? (
            <div className="space-y-3">
              {suggestions.map(sug => (
                <div key={sug.suggestion_id} className="bg-white rounded-2xl border border-[#E7E5E4] p-4" data-testid={`sug-${sug.suggestion_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-lg bg-[#ECFEFF] text-[#0891B2]">Suggestion</span>
                    <span className="text-[10px] text-[#A8A29E]">by {sug.submitter_name} &middot; {formatLevel(sug.submitter_level, 'short')}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {sug.direction === 'english_to_lamnso' ? sug.query_text : sug.suggested_translation}
                    </span>
                    <span className="text-[#A8A29E]">&rarr;</span>
                    <span className="text-base font-semibold text-[#4CAF50]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {sug.direction === 'english_to_lamnso' ? sug.suggested_translation : sug.query_text}
                    </span>
                  </div>
                  {sug.note && <p className="text-xs text-[#57534E] mb-2 italic">"{sug.note}"</p>}
                  {sug.example_context && <p className="text-[10px] text-[#A8A29E] mb-2">Example: {sug.example_context}</p>}
                  <div className="flex items-center justify-between pt-2 border-t border-[#E7E5E4]">
                    <div className="flex items-center gap-3">
                      <button onClick={() => voteSuggestion(sug.suggestion_id, 1)}
                        className={`flex items-center gap-1 text-xs font-medium transition-colors ${sug.user_vote === 1 ? 'text-[#588157]' : 'text-[#A8A29E] hover:text-[#588157]'}`}
                        data-testid={`upvote-${sug.suggestion_id}`}>
                        <ThumbsUp className={`w-4 h-4 ${sug.user_vote === 1 ? 'fill-current' : ''}`} /> Agree
                      </button>
                      <button onClick={() => voteSuggestion(sug.suggestion_id, -1)}
                        className={`flex items-center gap-1 text-xs font-medium transition-colors ${sug.user_vote === -1 ? 'text-[#4CAF50]' : 'text-[#A8A29E] hover:text-[#4CAF50]'}`}
                        data-testid={`downvote-${sug.suggestion_id}`}>
                        <ChevronDown className="w-4 h-4" /> Disagree
                      </button>
                    </div>
                    <div className="text-[10px] text-[#A8A29E]">
                      Score: <strong className={sug.vote_score > 0 ? 'text-[#588157]' : sug.vote_score < 0 ? 'text-[#4CAF50]' : 'text-[#57534E]'}>{sug.vote_score}</strong>
                      <span className="ml-1">(weighted: {sug.weighted_vote_score?.toFixed(1) || '0.0'})</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ThumbsUp className="w-10 h-10 text-[#E7E5E4] mx-auto mb-3" />
              <p className="text-sm text-[#57534E]">No pending suggestions</p>
              <p className="text-[10px] text-[#A8A29E] mt-1">Search for a word and suggest a translation if it's missing</p>
            </div>
          )}
        </div>
      ) : (
      /* FEED TAB */
      <div>

      {/* Compose */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-4" data-testid="compose-form">
        {showKeyboard ? (
          <div className="w-full border-0 resize-none text-sm text-[#1C1917] min-h-[60px] p-1 ring-2 ring-[#4CAF50] rounded-xl bg-white"
            style={{fontSize:'16px'}} data-testid="post-content-display">
            <span className="text-sm">{newPost || <span className="text-[#A8A29E]">Share a question, tip, proverb...</span>}</span>
            <span className="animate-pulse text-[#4CAF50]">|</span>
          </div>
        ) : (
          <textarea value={newPost} onChange={(e) => setNewPost(e.target.value)}
            placeholder="Share a question, tip, proverb, or learning moment..."
            className="w-full border-0 resize-none text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none min-h-[60px]"
            style={{fontSize:'16px'}}
            data-testid="post-content-input" />
        )}
        {postType === 'video_share' && (
          <input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="Paste YouTube URL..."
            className="w-full mt-2 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-[#FAFAF9]"
            data-testid="youtube-url-input" />
        )}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E7E5E4]">
          <div className="flex gap-1.5 overflow-x-auto">
            <button onClick={() => { setShowKeyboard(!showKeyboard); setActiveInput('post'); }}
              className={`text-[10px] px-2.5 py-1 rounded-lg font-bold whitespace-nowrap transition-colors ${
                showKeyboard ? 'bg-[#4CAF50] text-white' : 'bg-[#E8F5E9] text-[#4CAF50]'
              }`}
              data-testid="community-keyboard-toggle">
              {showKeyboard ? 'ABC' : 'əŋ'}
            </button>
            {typeButtons.map(t => {
              const cfg = TYPE_CONFIG[t] || TYPE_CONFIG.general;
              return (
                <button key={t} onClick={() => setPostType(t)}
                  className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                    postType === t ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E]'
                  }`}
                  data-testid={`type-${t}`}>
                  {cfg.label}
                </button>
              );
            })}
            <button onClick={() => setPostType('video_share')}
              className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                postType === 'video_share' ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E]'
              }`}>
              Video
            </button>
          </div>
          <Button onClick={handlePost} disabled={posting || !newPost.trim()} size="sm"
            className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs px-4" data-testid="submit-post-btn">
            {posting ? '...' : 'Post'}
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1" data-testid="post-filters">
        {filterButtons.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`text-[10px] px-3 py-1.5 rounded-xl font-semibold whitespace-nowrap transition-colors ${
              filter === f.key ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
            }`}
            data-testid={`filter-${f.key || 'all'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Posts */}
      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" /></div>
      ) : (
        <div className="space-y-3" data-testid="posts-list">
          {posts.map((post, idx) => {
            const banner = getBannerForPosition(idx, posts.length, banners);
            return (
              <React.Fragment key={post.post_id}>
                {banner && <PromoBanner banner={banner} />}
                <PostCard post={post} user={user} onLike={handleLike} onHelpful={handleHelpful}
                  expanded={expanded[post.post_id]} onToggle={() => setExpanded(p => ({ ...p, [post.post_id]: !p[post.post_id] }))}
                  formatTime={formatTime} />
              </React.Fragment>
            );
          })}
          {posts.length === 0 && <p className="text-center text-sm text-[#A8A29E] py-8">No posts yet. Start the conversation!</p>}
        </div>
      )}
      </div>
      )}

      {/* Lamnso Keyboard */}
      {showKeyboard && (
        <div className="mb-20">
          <LamnsoKeyboard
            onInsert={(char) => setNewPost(prev => prev + char)}
            onDelete={() => setNewPost(prev => prev.slice(0, -1))}
          />
        </div>
      )}
    </div>
  );
}

function PostCard({ post, user, onLike, onHelpful, expanded, onToggle, formatTime }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [acceptedId, setAcceptedId] = useState(post.accepted_comment_id);
  const cfg = TYPE_CONFIG[post.type] || TYPE_CONFIG.general;
  const TypeIcon = cfg.icon;

  const loadComments = async () => {
    try {
      const res = await api.get(`/community/posts/${post.post_id}/comments`);
      setComments(res.data.comments || []);
      setAcceptedId(res.data.accepted_comment_id);
    } catch (err) { console.error(err) }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await api.post(`/community/posts/${post.post_id}/comments`, {
        content: newComment, parent_comment_id: replyTo || ''
      });
      setNewComment('');
      setReplyTo(null);
      loadComments();
    } catch (err) { console.error(err) }
  };

  const handleAccept = async (commentId) => {
    try {
      await api.post(`/community/posts/${post.post_id}/accept/${commentId}`);
      setAcceptedId(commentId);
    } catch (err) { console.error(err) }
  };

  const handleToggle = () => {
    if (!expanded) loadComments();
    onToggle();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid={`post-${post.post_id}`}>
      <div className="p-4">
        {/* Author + type */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center text-xs font-bold text-[#4CAF50] flex-shrink-0">
            {post.author?.name?.charAt(0) || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-[#1C1917] truncate">{post.author?.name}</span>
              {post.author?.level >= 4 && (
                <span className="text-[9px] font-bold bg-[#D4A373] text-white px-1.5 py-0.5 rounded">Wirfon</span>
              )}
            </div>
            <span className="text-[10px] text-[#A8A29E]">{formatTime(post.created_at)} &middot; {formatLevel(post.author?.level || 1, 'short')}</span>
          </div>
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-lg ${cfg.color}`}>
            {cfg.label}
          </span>
        </div>

        {/* Content */}
        <p className="text-sm text-[#1C1917] leading-relaxed mb-2">{post.content}</p>

        {/* YouTube embed */}
        {post.youtube_id && (
          <div className="rounded-xl overflow-hidden mb-2 bg-black aspect-video" data-testid={`youtube-${post.post_id}`}>
            <iframe
              src={`https://www.youtube.com/embed/${post.youtube_id}`}
              title="YouTube video" frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen className="w-full h-full"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button onClick={() => onLike(post.post_id)}
            className={`flex items-center gap-1 text-xs font-medium transition-colors ${post.user_liked ? 'text-[#4CAF50]' : 'text-[#A8A29E]'}`}
            data-testid={`like-${post.post_id}`}>
            <Heart className={`w-4 h-4 ${post.user_liked ? 'fill-current' : ''}`} /> {post.likes_count || 0}
          </button>
          <button onClick={() => onHelpful(post.post_id)}
            className={`flex items-center gap-1 text-xs font-medium transition-colors ${post.user_helpful ? 'text-[#588157]' : 'text-[#A8A29E]'}`}
            data-testid={`helpful-${post.post_id}`}>
            <ThumbsUp className={`w-4 h-4 ${post.user_helpful ? 'fill-current' : ''}`} /> {post.helpful_count || 0}
          </button>
          <button onClick={handleToggle}
            className="flex items-center gap-1 text-xs font-medium text-[#A8A29E] hover:text-[#57534E]"
            data-testid={`comments-toggle-${post.post_id}`}>
            <MessageCircle className="w-4 h-4" /> {post.comment_count || 0}
          </button>
        </div>
      </div>

      {/* Comments */}
      {expanded && (
        <div className="border-t border-[#E7E5E4] bg-[#FAFAF9]">
          <div className="p-3 space-y-2.5">
            {comments.map(c => (
              <CommentItem key={c.comment_id} comment={c} isAccepted={acceptedId === c.comment_id}
                postUserId={post.user_id} currentUser={user} onAccept={handleAccept}
                onReply={() => setReplyTo(c.comment_id)} formatTime={formatTime} />
            ))}
          </div>
          <div className="p-3 pt-0">
            {replyTo && (
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-[#A8A29E]">Replying to comment</span>
                <button onClick={() => setReplyTo(null)} className="text-[10px] text-[#4CAF50]">Cancel</button>
              </div>
            )}
            <div className="flex gap-2">
              <input value={newComment} onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                placeholder={replyTo ? "Write a reply..." : "Write a comment..."}
                className="flex-1 rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
                data-testid={`comment-input-${post.post_id}`} />
              <button onClick={handleComment}
                className="p-2 rounded-xl bg-[#4CAF50] text-white hover:bg-[#3D8B40]"
                data-testid={`send-comment-${post.post_id}`}>
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommentItem({ comment, isAccepted, postUserId, currentUser, onAccept, onReply, formatTime }) {
  const isElderComment = comment.author?.level >= 4;
  const canAccept = currentUser && (currentUser.user_id === postUserId || currentUser.level >= 4);

  return (
    <div className={`${isAccepted ? 'bg-[#EAF1EA] border border-[#588157]/20 rounded-xl p-2.5' : ''}`}>
      <div className="flex gap-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
          isElderComment ? 'bg-[#D4A373] text-white' : 'bg-[#F5F5F4] text-[#57534E]'
        }`}>
          {comment.author?.name?.charAt(0) || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-[#1C1917]">{comment.author?.name}</span>
            {isElderComment && <span className="text-[8px] font-bold bg-[#D4A373] text-white px-1 py-0.5 rounded">Wirfon</span>}
            {isAccepted && <span className="text-[8px] font-bold bg-[#588157] text-white px-1 py-0.5 rounded flex items-center gap-0.5"><Check className="w-2 h-2" />Accepted</span>}
            <span className="text-[10px] text-[#A8A29E]">{formatTime(comment.created_at)}</span>
          </div>
          <p className="text-xs text-[#1C1917] mt-0.5 leading-relaxed">{comment.content}</p>
          <div className="flex items-center gap-2 mt-1">
            <button onClick={onReply} className="text-[10px] text-[#A8A29E] hover:text-[#57534E] font-medium">Reply</button>
            {canAccept && !isAccepted && (
              <button onClick={() => onAccept(comment.comment_id)} className="text-[10px] text-[#588157] hover:underline font-medium">Accept</button>
            )}
          </div>

          {/* Replies */}
          {comment.replies?.length > 0 && (
            <div className="mt-2 pl-2 border-l-2 border-[#E7E5E4] space-y-2">
              {comment.replies.map(r => (
                <div key={r.comment_id} className="flex gap-1.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 ${
                    r.author?.level >= 4 ? 'bg-[#D4A373] text-white' : 'bg-[#F5F5F4] text-[#57534E]'
                  }`}>{r.author?.name?.charAt(0) || '?'}</div>
                  <div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-semibold text-[#1C1917]">{r.author?.name}</span>
                      {r.author?.level >= 4 && <span className="text-[7px] font-bold bg-[#D4A373] text-white px-1 rounded">Wirfon</span>}
                    </div>
                    <p className="text-[10px] text-[#57534E]">{r.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
